import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import Papa from "papaparse";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import {
  FiBarChart2, FiMessageCircle, FiUsers,
  FiArrowUpRight, FiArrowDownRight
} from "react-icons/fi";
import { fetchSiteMetrics as fetchMockMetrics } from "../../data/mockMetrics";

const isProd = process.env.NODE_ENV === "production";

const trafficSources = [
  { name: "Google", value: 540 },
  { name: "Facebook", value: 320 },
  { name: "LinkedIn", value: 180 },
  { name: "Direct", value: 199 },
];

const pageVisits = [
  { page: "/home", visits: 120 },
  { page: "/about", visits: 95 },
  { page: "/services", visits: 72 },
  { page: "/contact", visits: 43 },
  { page: "/demo", visits: 28 },
];

const sessionsOverTime = [
  { day: "Mon", sessions: 180 },
  { day: "Tue", sessions: 220 },
  { day: "Wed", sessions: 190 },
  { day: "Thu", sessions: 240 },
  { day: "Fri", sessions: 320 },
  { day: "Sat", sessions: 275 },
  { day: "Sun", sessions: 300 },
];

const COLORS = ["#6366f1", "#3b82f6", "#0ea5e9", "#22c55e"];

const AdminHomepage = () => {
  const [metrics, setMetrics] = useState([]);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    const loadMetrics = async () => {
      const fetchSiteMetrics = isProd
        ? async () => {
            const response = await fetch("/api/metrics");
            const data = await response.json();
            return data;
          }
        : fetchMockMetrics;

      const data = await fetchSiteMetrics();

      const dynamicMetrics = [
        {
          title: "Site Sessions",
          value: data.siteSessions,
          icon: <FiBarChart2 className="text-blue-500 text-3xl" />,
          trend: "+12%",
          trendDirection: "up",
          bg: "from-blue-500/10 to-blue-500/5",
        },
        {
          title: "AI Interactions",
          value: data.aiInteractions,
          icon: <FiMessageCircle className="text-indigo-500 text-3xl" />,
          trend: "+9%",
          trendDirection: "up",
          bg: "from-indigo-500/10 to-indigo-500/5",
        },
        {
          title: "Leads Captured",
          value: data.leadsCaptured,
          icon: <FiUsers className="text-green-500 text-3xl" />,
          trend: "+5 new today",
          trendDirection: "up",
          bg: "from-green-500/10 to-green-500/5",
        },
        {
          title: "Top Public Page",
          value: data.topPage,
          icon: <FiBarChart2 className="text-yellow-500 text-3xl" />,
          trend: "Most viewed today",
          trendDirection: "up",
          bg: "from-yellow-500/10 to-yellow-500/5",
        },
      ];

      setMetrics(dynamicMetrics);
    };

    const loadCSV = (path) =>
      new Promise((resolve, reject) => {
        Papa.parse(path, {
          download: true,
          header: true,
          complete: (results) => resolve(results.data),
          error: (err) => reject(err),
        });
      });

    loadMetrics();
    loadCSV("/docs/blackbear_production_tasks.csv").then(setTasks);
  }, []);

  return (
    <main className="flex-1 p-8 bg-neutral-950 min-h-screen">
      <h1 className="text-3xl font-bold text-white mb-2">Welcome back, Admin 👋</h1>
      <p className="text-gray-400 mb-6">Here’s what’s happening with your site today.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        {metrics.map((item, index) => (
          <div
            key={index}
            className={`bg-gradient-to-br ${item.bg} p-6 rounded-xl shadow-sm border border-white/10 transition hover:shadow-md hover:scale-[1.01]`}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-white/80">{item.title}</h2>
              {item.icon}
            </div>
            <p className="text-3xl font-bold text-white">
              {typeof item.value === "number" ? (
                <CountUp end={item.value} duration={1.4} separator="," />
              ) : (
                item.value
              )}
            </p>
            <div className="flex items-center gap-1 mt-1 text-sm">
              {item.trendDirection === "up" ? (
                <FiArrowUpRight className="text-green-400" />
              ) : (
                <FiArrowDownRight className="text-red-400" />
              )}
              <span
                className={`$${
                  item.trendDirection === "up" ? "text-green-400" : "text-red-400"
                }`}
              >
                {item.trend}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-neutral-900 p-6 rounded-xl shadow border border-neutral-800">
          <h3 className="text-lg font-semibold text-white mb-4">Sessions This Week</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={sessionsOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2c2c2c" />
              <XAxis dataKey="day" stroke="#999" />
              <YAxis stroke="#999" />
              <Tooltip />
              <Line type="monotone" dataKey="sessions" stroke="#3b82f6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-neutral-900 p-6 rounded-xl shadow border border-neutral-800">
          <h3 className="text-lg font-semibold text-white mb-4">Traffic Sources</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={trafficSources}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {trafficSources.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: "#1f2937", border: "none" }}
                labelStyle={{ color: "#fff" }}
                itemStyle={{ color: "#d1d5db" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-neutral-900 p-6 rounded-xl shadow border border-neutral-800 lg:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4">Top Page Visits</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={pageVisits}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2c2c2c" />
              <XAxis dataKey="page" stroke="#999" />
              <YAxis stroke="#999" />
              <Tooltip />
              <Bar dataKey="visits" fill="#6366f1" barSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-12 bg-neutral-900 border border-neutral-800 rounded-xl shadow p-6">
  <h3 className="text-lg font-semibold text-white mb-4">🗂️ Production Checklist</h3>
  <div className="space-y-4">
    {tasks.length === 0 ? (
      <p className="text-gray-500 text-sm">No tasks loaded. Please check the CSV file.</p>
    ) : (
      tasks.map((task, i) => (
        <div
          key={i}
          className="bg-neutral-800 rounded-xl px-4 py-3 border border-neutral-700 flex flex-col md:flex-row md:items-center md:justify-between gap-4 transition hover:border-blue-500"
        >
          {/* Left: Checkbox and Info */}
          <div className="flex items-center gap-3 w-full md:w-1/2">
            <input
              type="checkbox"
              className="form-checkbox text-green-500 rounded border-gray-600"
              onChange={() => {
                const updated = [...tasks];
                updated[i].completed = !updated[i].completed;
                setTasks(updated);
              }}
              checked={task.completed || false}
            />
            <div>
              <h4 className={`text-white font-medium ${task.completed ? "line-through opacity-60" : ""}`}>
                {task.Task}
              </h4>
              <p className="text-xs text-gray-400">
                Start: {task.Start} — Finish: {task.Finish}
              </p>
            </div>
          </div>

          {/* Right: Visual Progress Bar */}
          <div className="w-full md:w-1/2">
            <div className="relative w-full bg-gray-700/30 h-2 rounded-full overflow-hidden">
              <div
                className={`h-2 ${task.completed ? "bg-green-500" : "bg-blue-500"}`}
                style={{ width: "100%" }} // You can add % logic here later
              ></div>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
</div>

</main>
);
};

export default AdminHomepage;
